import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { ControlValueAccessor, Form, FormControl, FormGroup, NG_VALUE_ACCESSOR } from "@angular/forms";

// ---------------------------------------------------------------------------
// Type declarations – mirrors ngx-google-places-autocomplete public API so
// this component is a drop-in replacement.
// ---------------------------------------------------------------------------

export interface ComponentRestrictions {
  country: string | string[];
}

export interface LatLngBounds {
  northeast: { lat: number; lng: number };
  southwest: { lat: number; lng: number };
}

/** Subset of google.maps.places.AutocompleteOptions */
export interface PlacesAutocompleteOptions {
  /** Restricts predictions to specific country/countries (ISO 3166-1 alpha-2). */
  componentRestrictions?: ComponentRestrictions;
  /** Bounding box to bias results toward. */
  bounds?: LatLngBounds;
  /** 'address' | 'establishment' | 'geocode' | '(cities)' | '(regions)' */
  types?: string[];
  /** Fields to retrieve from the Places API (controls billing). */
  fields?: string[];
  /** BCP-47 language code for result language. */
  language?: string;
  /** Strict bounds mode – only return results within bounds. */
  strictBounds?: boolean;
}

export interface AddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface PlaceResult {
  address_components?: AddressComponent[];
  formatted_address?: string;
  geometry?: {
    location?: {
      lat(): number;
      lng(): number;
    };
  };
  name?: string;
  place_id?: string;
  types?: string[];
  [key: string]: unknown;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * AddressAutocompleteComponent
 *
 * A standalone Angular 16–20 compatible component that wraps the Google Places
 * Autocomplete widget. Its public API is intentionally compatible with
 * ngx-google-places-autocomplete so it can serve as a drop-in replacement.
 *
 * Usage
 * -----
 * Make sure the Maps JavaScript API (with the "places" library) is loaded
 * before this component initialises, e.g. via index.html:
 *
 *   <script src="https://maps.googleapis.com/maps/api/js?key=YOUR_KEY&libraries=places"></script>
 *
 * Template
 * --------
 *   <app-address-autocomplete
 *     [(ngModel)]="address"
 *     [options]="{ componentRestrictions: { country: 'us' } }"
 *     (onAddressChange)="onAddressChange($event)"
 *   ></app-address-autocomplete>
 *
 * Or with Reactive Forms:
 *   <app-address-autocomplete
 *     formControlName="address"
 *     [options]="autocompleteOptions"
 *     (onAddressChange)="onAddressChange($event)"
 *   ></app-address-autocomplete>
 */
@Component({
    selector: 'app-address-autocomplete',
    templateUrl: 'address-autocomplete.component.html',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AddressAutocompleteComponent),
            multi: true,
        },
    ],
    styleUrls: ['./address-autocomplete.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AddressAutocompleteComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  // -------------------------------------------------------------------------
  // Inputs / Outputs  (ngx-google-places-autocomplete compatible)
  // -------------------------------------------------------------------------

  /** Google Places Autocomplete options. */
  @Input() options: PlacesAutocompleteOptions = {};

  /** Input placeholder text. */
  @Input() placeholder = 'Enter an address';
  @Input() formControlName: string;
  @Input() form: FormGroup;
  /**
   * Emitted whenever the user selects a suggestion from the dropdown.
   * Provides the full PlaceResult object.
   * Named `onAddressChange` to match ngx-google-places-autocomplete.
   */
  @Output() onAddressChange = new EventEmitter<PlaceResult>();

  // -------------------------------------------------------------------------
  // Internal state
  // -------------------------------------------------------------------------

  @ViewChild('addressInput', { static: true })
  addressInput!: ElementRef<HTMLInputElement>;

  inputValue = '';
  isDisabled = false;

  private autocomplete: google.maps.places.Autocomplete | null = null;
  private placeChangedListener: google.maps.MapsEventListener | null = null;

  // ControlValueAccessor callbacks
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange: (value: string) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouched: () => void = () => {};

  constructor(private readonly ngZone: NgZone) {}

  // -------------------------------------------------------------------------
  // Lifecycle
  // -------------------------------------------------------------------------

  ngOnInit(): void {
    this.initAutocomplete();
  }

  ngOnDestroy(): void {
    this.destroyAutocomplete();
  }

  // -------------------------------------------------------------------------
  // Public API (ngx-google-places-autocomplete compatible)
  // -------------------------------------------------------------------------

  /** Resets the input value programmatically. */
  reset(): void {
    this.inputValue = '';
    this.addressInput.nativeElement.value = '';
    this.onChange('');
  }

  // -------------------------------------------------------------------------
  // ControlValueAccessor
  // -------------------------------------------------------------------------

  writeValue(value: string): void {
    this.inputValue = value ?? '';
    if (this.addressInput?.nativeElement) {
      this.addressInput.nativeElement.value = this.inputValue;
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
  }

  // -------------------------------------------------------------------------
  // Internal helpers
  // -------------------------------------------------------------------------

  onInputChange(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    this.inputValue = value;
    this.onChange(value);
  }

  private initAutocomplete(): void {
    if (typeof google === 'undefined' || !google?.maps?.places?.Autocomplete) {
      console.warn(
        '[AddressAutocomplete] Google Maps JavaScript API (places library) ' +
        'not found. Autocomplete will not function until the API is loaded.'
      );
      return;
    }

    const autocompleteOptions: google.maps.places.AutocompleteOptions =
      this.buildAutocompleteOptions();

    this.autocomplete = new google.maps.places.Autocomplete(
      this.addressInput.nativeElement,
      autocompleteOptions
    );

    this.placeChangedListener = this.autocomplete.addListener(
      'place_changed',
      () => {
        // Run inside Angular's zone so change detection fires.
        this.ngZone.run(() => {
          const place = this.autocomplete!.getPlace() as PlaceResult;

          if (!place || (!place.address_components && !place.formatted_address)) {
            // User pressed Enter without selecting a suggestion.
            return;
          }

          // Sync the formatted address string back to the form control.
          const formatted = place.formatted_address ?? '';
          this.inputValue = formatted;
          this.onChange(formatted);

          this.onAddressChange.emit(place);
        });
      }
    );
  }

  private destroyAutocomplete(): void {
    if (this.placeChangedListener) {
      this.placeChangedListener.remove();
      this.placeChangedListener = null;
    }
    // Remove the pac-container appended to the body by the Google SDK.
    if (this.autocomplete) {
      google.maps.event.clearInstanceListeners(this.autocomplete);
      this.autocomplete = null;
    }
  }

  private buildAutocompleteOptions(): google.maps.places.AutocompleteOptions {
    const opts: google.maps.places.AutocompleteOptions = {};

    if (this.options.types?.length) {
      opts.types = this.options.types;
    }

    if (this.options.componentRestrictions) {
      opts.componentRestrictions = this.options
        .componentRestrictions as google.maps.places.ComponentRestrictions;
    }

    if (this.options.fields?.length) {
      opts.fields = this.options.fields;
    } else {
      // Default fields – limits data returned to keep API calls cheaper.
      opts.fields = [
        'address_components',
        'formatted_address',
        'geometry',
        'name',
        'place_id',
        'types',
      ];
    }

    if (this.options.strictBounds !== undefined) {
      opts.strictBounds = this.options.strictBounds;
    }

    return opts;
  }
}
